import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  SelectChangeEvent,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

interface ProductionData {
  dia: number;
  weight: number;
}

interface DailyProductionData {
  date: string;
  weight: number;
}

interface MetricsResponse {
  start_date: string;
  end_date: string;
  data: {
    '14': ProductionData[];
    '16': ProductionData[];
    '24': ProductionData[];
  };
}

interface DailyMetricsResponse {
  start_date: string;
  end_date: string;
  data: {
    '14': DailyProductionData[];
    '16': DailyProductionData[];
    '24': DailyProductionData[];
  };
}

type DateRange = 'week' | 'month' | 'year' | 'custom';

const ProductionMetrics: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [metricsData, setMetricsData] = useState<MetricsResponse | null>(null);
  const [dailyMetricsData, setDailyMetricsData] = useState<DailyMetricsResponse | null>(null);
  const [dateRange, setDateRange] = useState<DateRange>('week');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  const fetchMetrics = async (startDate: string, endDate: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: token || '' };
      
      // Fetch both production by dia and daily production data
      const [productionResponse, dailyResponse] = await Promise.all([
        fetch(
          `${API_URL}/metrics/production?start_date=${startDate}&end_date=${endDate}`,
          { headers }
        ),
        fetch(
          `${API_URL}/metrics/production_daily?start_date=${startDate}&end_date=${endDate}`,
          { headers }
        ),
      ]);

      if (!productionResponse.ok) {
        throw new Error('Failed to fetch production metrics data');
      }
      if (!dailyResponse.ok) {
        throw new Error('Failed to fetch daily metrics data');
      }

      const productionData = await productionResponse.json();
      const dailyData = await dailyResponse.json();
      
      setMetricsData(productionData);
      setDailyMetricsData(dailyData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const calculateDateRange = (range: DateRange): { start: string; end: string } => {
    const today = new Date();
    const end = today.toISOString().split('T')[0];
    let start: Date;

    switch (range) {
      case 'week':
        start = new Date(today);
        start.setDate(today.getDate() - 7);
        break;
      case 'month':
        start = new Date(today);
        start.setMonth(today.getMonth() - 1);
        break;
      case 'year':
        start = new Date(today);
        start.setFullYear(today.getFullYear() - 1);
        break;
      case 'custom':
        return { start: customStartDate, end: customEndDate };
      default:
        start = new Date(today);
        start.setDate(today.getDate() - 7);
    }

    return { start: start.toISOString().split('T')[0], end };
  };

  useEffect(() => {
    if (dateRange !== 'custom' || (customStartDate && customEndDate)) {
      const { start, end } = calculateDateRange(dateRange);
      if (start && end) {
        fetchMetrics(start, end);
      }
    }
  }, [dateRange, customStartDate, customEndDate]);

  const handleDateRangeChange = (event: SelectChangeEvent<DateRange>) => {
    setDateRange(event.target.value as DateRange);
  };

  const prepareChartData = (data: ProductionData[]) => {
    // Sort by dia for proper bar chart display (create a copy to avoid mutating read-only array)
    return [...data].sort((a, b) => a.dia - b.dia);
  };

  const formatYAxis = (value: number) => {
    return `${value.toFixed(0)} kg`;
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const renderDailyChart = (gauge: '14' | '16' | '24', title: string, color: string) => {
    if (!dailyMetricsData || !dailyMetricsData.data[gauge]) {
      return null;
    }

    const chartData = dailyMetricsData.data[gauge];
    const hasData = chartData.some(d => d.weight > 0);

    return (
      <Grid item xs={12} key={`daily-${gauge}`}>
        <Card elevation={2}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
              {title} - Daily Trend
            </Typography>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart
                data={chartData}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  label={{ value: 'Date', position: 'insideBottom', offset: -5 }}
                  tickFormatter={formatDate}
                  tick={{ fontSize: 12 }}
                  interval={0}
                  angle={-45}
                  textAnchor="end"
                  height={60}
                />
                <YAxis
                  label={{ value: 'Weight (kg)', angle: -90, position: 'insideLeft' }}
                  tickFormatter={formatYAxis}
                />
                <Tooltip
                  formatter={(value) => [`${(Number(value) || 0).toFixed(2)} kg`, 'Weight']}
                  labelFormatter={(label) => `Date: ${formatDate(label as string)}`}
                />
                <Legend />
                <Bar
                  dataKey="weight"
                  fill={color}
                  name="Daily Production (kg)"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
            {!hasData && (
              <Typography
                variant="body2"
                color="text.secondary"
                align="center"
                sx={{ mt: 2 }}
              >
                No production data available for this period
              </Typography>
            )}
          </CardContent>
        </Card>
      </Grid>
    );
  };

  const renderChart = (gauge: '14' | '16' | '24', title: string, color: string) => {
    if (!metricsData || !metricsData.data[gauge]) {
      return null;
    }

    const chartData = prepareChartData(metricsData.data[gauge]);

    // If no data, create placeholder with all dias at 0
    const hasData = chartData.some(d => d.weight > 0);
    
    return (
      <Grid item xs={12} key={gauge}>
        <Card elevation={2}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
              {title}
            </Typography>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart
                data={chartData}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="dia"
                  label={{ value: 'Dia', position: 'insideBottom', offset: -5 }}
                  tickFormatter={(value) => value.toString()}
                />
                <YAxis
                  label={{ value: 'Weight (kg)', angle: -90, position: 'insideLeft' }}
                  tickFormatter={formatYAxis}
                />
                <Tooltip
                  formatter={(value) => [`${(Number(value) || 0).toFixed(2)} kg`, 'Weight']}
                  labelFormatter={(label) => `Dia: ${label}`}
                />
                <Legend />
                <Bar
                  dataKey="weight"
                  fill={color}
                  name="Production (kg)"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
            {!hasData && (
              <Typography
                variant="body2"
                color="text.secondary"
                align="center"
                sx={{ mt: 2 }}
              >
                No production data available for this period
              </Typography>
            )}
          </CardContent>
        </Card>
      </Grid>
    );
  };

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
        Production Metrics
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth>
              <InputLabel>Date Range</InputLabel>
              <Select
                value={dateRange}
                label="Date Range"
                onChange={handleDateRangeChange}
              >
                <MenuItem value="week">Last Week</MenuItem>
                <MenuItem value="month">Last Month</MenuItem>
                <MenuItem value="year">Last Year</MenuItem>
                <MenuItem value="custom">Custom Range</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {dateRange === 'custom' && (
            <>
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth>
                  <Typography variant="caption" sx={{ mb: 0.5 }}>
                    Start Date
                  </Typography>
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    style={{
                      padding: '16.5px 14px',
                      fontSize: '1rem',
                      border: '1px solid #c4c4c4',
                      borderRadius: '4px',
                      width: '100%',
                      fontFamily: 'inherit',
                    }}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth>
                  <Typography variant="caption" sx={{ mb: 0.5 }}>
                    End Date
                  </Typography>
                  <input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    style={{
                      padding: '16.5px 14px',
                      fontSize: '1rem',
                      border: '1px solid #c4c4c4',
                      borderRadius: '4px',
                      width: '100%',
                      fontFamily: 'inherit',
                    }}
                  />
                </FormControl>
              </Grid>
            </>
          )}
        </Grid>

        {metricsData && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Showing data from {new Date(metricsData.start_date).toLocaleDateString()} to{' '}
              {new Date(metricsData.end_date).toLocaleDateString()}
            </Typography>
          </Box>
        )}
      </Paper>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && metricsData && (
        <Grid container spacing={3}>
          {/* 14 GG Charts */}
          {renderChart('14', '14 GG Production by Dia', '#90caf9')}
          {renderDailyChart('14', '14 GG Production', '#90caf9')}
          
          {/* 16 GG Charts */}
          {renderChart('16', '16 GG Production by Dia', '#00acc1')}
          {renderDailyChart('16', '16 GG Production', '#00acc1')}
          
          {/* 24 GG Charts */}
          {renderChart('24', '24 GG Production by Dia', '#ff9e80')}
          {renderDailyChart('24', '24 GG Production', '#ff9e80')}
        </Grid>
      )}
    </Box>
  );
};

export default ProductionMetrics;

